import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IObservableArray } from 'mobx';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IImportColumn, Importer } from '@feathr/blackbox';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardV2,
  TanStackTable,
  Text,
} from '@feathr/components';

import { countMappedColumns } from './DataImportStepThree.utils';
import { getNextUnmappedColumn } from './DataImportStepTwo.utils';
import { importMappingColumns } from './ImportMappingTable.columns';
import type { IImportMappingRow } from './ImportMappingTable.utils';
import { toMappingRows } from './ImportMappingTable.utils';

import * as styles from './ImportMappingTable.css';

interface IProps {
  importer: Importer;
  disabled?: boolean;
  /** First value of each column in the preview; undefined until the first preview arrives. */
  samples?: Record<string, string>;
}

/**
 * Step 2's mappings, in the integrations' mapping-table pattern: a full-width card with a table of
 * file column → sample → Feathr field rows, and Add column in its footer.
 */
function ImportMappingTable({ importer, disabled = false, samples }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const columnMappers = importer.get('column_mappers') as IObservableArray<IImportColumn>;
  // Rows whose column was cleared out aren't mappings yet, so they aren't shown.
  const rows = toMappingRows(columnMappers);
  // Counted over the file's columns, the same as the Review step, so removing a row lowers the
  // mapped count rather than the total.
  const { mapped, total } = countMappedColumns(importer.get('column_names') ?? [], columnMappers);
  // The cells observe their own mapper, so the definitions only change with what they're built from.
  const columns = useMemo(
    () => importMappingColumns({ t, importer, disabled, samples }),
    [t, importer, disabled, samples],
  );
  const nextColumn = getNextUnmappedColumn(importer.get('column_names') ?? [], columnMappers);

  function handleAddColumn(): void {
    if (nextColumn === undefined) {
      return;
    }
    runInAction(() => {
      columnMappers.push(
        observable({ column_name: nextColumn, feathr_attr: undefined, attr_type: undefined }),
      );
    });
    importer.setAttributeDirty('column_mappers');
  }

  let addDisabledReason: string | undefined;
  if (disabled) {
    addDisabledReason = t('This import can no longer be changed.');
  } else if (nextColumn === undefined) {
    addDisabledReason = t('Every column in your file is mapped.');
  }

  return (
    <CardV2 name={'import-mapping'} width={'full'}>
      <CardHeader
        description={t('Match each column in your file to a field in Feathr.')}
        title={t('Columns')}
      >
        <Text.SmallRegular className={styles.counter}>
          {t('{{mapped}} of {{total}} columns mapped', {
            mapped,
            total,
          })}
        </Text.SmallRegular>
      </CardHeader>
      <CardContent>
        <TanStackTable<IImportMappingRow>
          columns={columns}
          idKey={'id'}
          isPaginated={false}
          items={rows}
          noDataText={t('No columns mapped yet. Add a column to map it to a Feathr field.')}
        />
      </CardContent>
      <CardActions contentClassName={styles.actions}>
        <Button
          disabled={Boolean(addDisabledReason)}
          onClick={handleAddColumn}
          prefix={<FontAwesomeIcon icon={faPlus} />}
          tooltip={addDisabledReason}
        >
          {t('Add column')}
        </Button>
      </CardActions>
    </CardV2>
  );
}

export default observer(ImportMappingTable);
